import React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import EmployeeDeactivateCell from '../users/EmployeeDeactivateCell';
import { useDeactivateEmployee } from '../../hooks/useCompanyUsers';
import { useUsersForRole } from '../../hooks/usePermissions';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../query/queryKeys';

function RoleUsersDialog({ open, role, onClose }) {
    const roleId = role?.id;
    const { data: users = [], isLoading } = useUsersForRole(roleId, { enabled: open && Boolean(roleId) });
    const deactivateEmployeeMutation = useDeactivateEmployee();
    const queryClient = useQueryClient();

    function deactivateEmployee(cellValues) {
        deactivateEmployeeMutation.mutate(
            { Id: cellValues.row.id, CompanyId: cellValues.row.companyId },
            {
                onSuccess: () => {
                    void queryClient.invalidateQueries({ queryKey: queryKeys.permissions.roleUserCounts() });
                    void queryClient.invalidateQueries({ queryKey: queryKeys.permissions.roleUsers(roleId) });
                },
            },
        );
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Users with role: {role?.name ?? ''}
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Active user count excludes deactivated accounts. Deactivated users remain listed here so you can reactivate them.
                </Typography>
                {isLoading &&
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={40} color="warning" />
                    </Box>
                }
                {!isLoading && users.length === 0 &&
                    <Typography variant="body2">No users are assigned to this role.</Typography>
                }
                {!isLoading && users.length > 0 &&
                    <Box sx={{ '& .header': { backgroundColor: '#e2e2e2' } }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell className="header">Company</TableCell>
                                    <TableCell className="header">Username</TableCell>
                                    <TableCell className="header" width={150}>Deactivate</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users.map((user) => {
                                    const row = {
                                        id: user.userId,
                                        userName: user.userName,
                                        userRole: user.userRole,
                                        deleted: user.deleted,
                                        companyId: user.companyId,
                                    };
                                    const cellValues = { row };

                                    return (
                                        <TableRow
                                            key={`${user.userId}-${user.companyId}`}
                                            sx={user.deleted ? { opacity: 0.65 } : undefined}
                                        >
                                            <TableCell>{user.companyName}</TableCell>
                                            <TableCell>{user.userName}</TableCell>
                                            <TableCell>
                                                <EmployeeDeactivateCell
                                                    cellValues={cellValues}
                                                    onDeactivate={deactivateEmployee}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </Box>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

export default RoleUsersDialog;
